import Big from 'big.js';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import {
  AccountDataPoint,
  CompanyFinancialMetricsTableQuery,
  GlAccountFragFragment,
  useCompanyFinancialMetricsTableQuery
} from 'generated/graphql';



export interface IData {
  years: number[];
  pricingSection: GlAccountFragFragment[];
  financialsSection: GlAccountFragFragment[];
  ratioSection: GlAccountFragFragment[];
}

export function useQuery(listedCompanyId?: string) {
  const years = useMemo(() => {
    const year = DateTime.now().year;

    return _.times(8, (i) => year - i);
  }, []);

  const tuple = useCompanyFinancialMetricsTableQuery({
    returnPartialData: true,
    skip: !listedCompanyId,
    variables: {
      id: listedCompanyId ?? '',
      years
    }
  });

  return useMemo(
    () => {
      const accounts = tuple.data?.listedCompany?.standardGlAccounts;
      const data: IData = {
        years: [],
        financialsSection: [],
        pricingSection: [],
        ratioSection: []
      };
      if (!accounts) {
        return {
          ...tuple,
          data
        };
      }

      //   'priceChange', (Missing)
      //   'indexPrice', (Missing)
      //   'industryPrice', (Missing)

      data.financialsSection = _.compact([
        accounts['totalrevenue'],
        accounts['ebitda'],
        accounts['ebit'],
        accounts['netincome'],
        accounts['dilutedeps'],
        accounts['cashdividendspershare'],
        accounts['adjweightedavedilutedsharesos'],
        accounts['bookvaluepershare'],
        accounts['cashandequivalents'],
        accounts['totalassets'],
        accounts['networkingcapital'],
        accounts['longtermdebt'],
        accounts['netcashfromoperatingactivities'],
        accounts['capex'],
        accounts['freecashflow']
      ]);

      data.ratioSection = _.compact([
        accounts['grossmargin'],
        accounts['ebitdamargin'],
        accounts['ebitmargin'],
        accounts['profitmargin'],
        accounts['roa'],
        accounts['roe'],
        accounts['assetturnover'],
        //   'assetsToTotalEquityRatio', (Missing)
        //   'salesToEmployeeRatio', (Missing)

        accounts['dso'],
        accounts['dpo'],
        accounts['currentratio'],
        accounts['quickratio']
        // accounts['debttototalcapital'], // Removed 04-23-2025
        // accounts['debttoequity'] // Removed 04-23-2025
      ]);

      const pickedAccounts = [
        // ...data.pricingSection, // Don't include pricing because it needs to be consistent with GLs.
        ...data.financialsSection,
        ...data.ratioSection
      ];

      const allYears = pickedAccounts.flatMap((v) => v.yearsContainingData);
      data.years = _.sortBy(_.uniq(allYears));

      const comparisonToSAndPAsGl: GlAccountFragFragment = __makeComparisonToSAndPAsGl(data.years, tuple.data);
      const priceChange: GlAccountFragFragment = __makePriceChangeAsGl(data.years, tuple.data);

      data.pricingSection = _.compact([
        priceChange,
        comparisonToSAndPAsGl,
        accounts['dividendyield']
      ]);

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



function __calculateGrowthRates(glYears: number[], dataPoints: Array<{
  date: DateTime;
  value: Big;
}>) {
  const setOfYears = new Set(glYears);
  const filtered = dataPoints.filter((dp) => {
    const hasCurrentYear = setOfYears.has(dp.date.year);
    return hasCurrentYear || setOfYears.has(dp.date.year + 1);
  });
  const year2DataPoints = _.groupBy(filtered, (f) => f.date.year);
  const year2LastValue = _.mapValues(year2DataPoints, (dp) => {
    const last = _.last(dp);
    if (!last) {
      throw new Error('Tried to get last from empty array.');
    }
    return last;
  });

  const yearsInDataSet = _.keys(year2DataPoints).map((year) => _.toNumber(year));

  return yearsInDataSet.map((year) => {
    const curr = year2LastValue[year.toString()];
    const prev = year2LastValue[(year - 1).toString()];

    try {
      if (curr && prev) {
        const growth = prev.value.eq(0) ? null : curr.value.minus(prev.value).div(prev.value);
        return {
          year,
          growth
        };
      }

    } catch (err) {
      return {
        year,
        growth: null
      };
    }
    return {
      year,
      growth: null
    };
  });
}



function __makeComparisonToSAndPAsGl(glYears: number[], data?: CompanyFinancialMetricsTableQuery): GlAccountFragFragment {
  const macroIndex = data?.economicIndices.find((n) => n.fredCode === 'SPX');
  const historicalStockPrices = data?.historicalStockPrice;

  const indexGrowthRates = __calculateGrowthRates(glYears, macroIndex?.historicalData ?? []);
  const priceGrowthRates = __calculateGrowthRates(glYears, historicalStockPrices ?? []);

  const growthRatesComparisonValues = glYears.map((year): AccountDataPoint | null => {
    const indexValue = indexGrowthRates.find((v) => v.year === year);
    const priceValue = priceGrowthRates.find((v) => v.year === year);

    const changeFromSp = (!_.isNil(indexValue?.growth) && !_.isNil(priceValue?.growth))
      ? priceValue.growth.minus(indexValue.growth)
      : null;

    if (changeFromSp === null) {
      return null;
    }

    return {
      year: _.toNumber(year),
      value: changeFromSp ?? null,
      period: 'fy',
      formattedValue: changeFromSp.mul(100).toFixed(2) + '%',
      formattedValueForFinance: changeFromSp.mul(100).toFixed(2) + '%',
      unit: 'percentage'
    };
  });

  const fiscalYearValues = _.compact(growthRatesComparisonValues);
  const avg = __calculateFiveYearAverage(fiscalYearValues);

  return {
    chatDisplayName: '',
    financialsDisplayName: '+/- S&P 500 (%)',
    values: [],
    displayName: 'Comparison to S&P 500',
    fiscalYearValues,
    id: '',
    name: '',
    fiveYearAverage: avg?.mul(100),
    formattedFiveYearAverage: avg?.mul(100).toFixed(2) + ' %',
    yearsContainingData: _.uniq(fiscalYearValues.map((y) => _.toNumber(y))),
    definition: '',
    formula: '\\pm S\\&P 500 (\\%)=(Stock Price Growth Rate-S\\&P 500 Growth Rate)\\times{100}'
  };
}



function __calculateFiveYearAverage(dataPoints: AccountDataPoint[]) {
  const vals = dataPoints.length > 5
    ? dataPoints.slice(dataPoints.length - 5, dataPoints.length)
    : dataPoints;

  const sum = vals.reduce((sum, fyValue) => sum.plus(fyValue.value), Big(0));
  const avg = vals.length === 0 ? null : sum.div(vals.length);
  return avg;
}



// TODO: We should be able to use the work here above in the comparisons as well.
function __makePriceChangeAsGl(glYears: number[], data?: CompanyFinancialMetricsTableQuery): GlAccountFragFragment {
  const historicalStockPrices = data?.historicalStockPrice;
  const priceGrowthRates = __calculateGrowthRates(glYears, historicalStockPrices ?? []);

  const fiscalYearValues = priceGrowthRates.filter((v) => !_.isNil(v.growth)).map((val): AccountDataPoint => ({
    formattedValue: val.growth?.mul(100).toFixed(2) + '%',
    formattedValueForFinance: val.growth?.mul(100).toFixed(2) + '%',
    period: 'fy',
    unit: 'percentage',
    value: val.growth ?? Big(0),
    year: _.toNumber(val.year)
  }));

  const avg = __calculateFiveYearAverage(fiscalYearValues);

  return {
    chatDisplayName: '',
    financialsDisplayName: 'Price Change (%)',
    values: [],
    displayName: 'Price Change',
    fiscalYearValues,
    id: '',
    name: 'percent_change',
    fiveYearAverage: avg,
    formattedFiveYearAverage: avg?.mul(100).toFixed(2) + ' %',
    yearsContainingData: _.uniq(priceGrowthRates.map((y) => y.year)),
    definition: '',
    formula: 'Price Change (\\%)=(\\frac{Current Year Price}{Previous Year Price}-1)\\times{100}'
  };
}
